/**
 * What an ingredient costs, worked out rather than typed — FRONTEND.md §9.227, BACKEND.md §10A.90.
 *
 * *"So no manual pricing, only merná jednotka and množstvo is basically in hmotnosti calculated based
 * from ingrediencies (or manually adjusted)."*
 *
 * **The parenthesis is load-bearing.** On the owner's data **0 of 130** active stock items carries a
 * `cost_price`, so a derivation that always produced a number would print `0,00 €` for every
 * ingredient and call it a cost. This answers `null` when there is nothing to derive from, and the
 * editor says so — which is what legacy does with `ingredients_with_cost` against `ingredient_count`.
 */

/** How many of a unit go into one of another, within the same kind of measure. */
const PER_UNIT: Record<string, number> = {
  g: 1,
  kg: 1000,
  ml: 1,
  l: 1000,
  dl: 100,
  ks: 1,
  liter: 1000,
  ks_: 1,
}

/** Mass, volume and count do not convert into one another. */
const FAMILY: Record<string, string> = {
  g: 'mass',
  kg: 'mass',
  ml: 'volume',
  l: 'volume',
  dl: 'volume',
  liter: 'volume',
  ks: 'count',
}

function normalise(unit: string | null | undefined): string {
  return (unit ?? '').trim().toLowerCase()
}

/**
 * The factor that turns a quantity in `from` into one in `to`, or `null` when it cannot.
 *
 * A price quoted per kilogram against a quantity in grams is the case this exists for — the stock
 * screen already warns about that mismatch, and multiplying across it would be a confident wrong
 * number rather than a missing one.
 */
export function conversionFactor(from: string | null | undefined, to: string | null | undefined): number | null {
  const source = normalise(from)
  const target = normalise(to)
  if (source === '' || target === '') return null
  if (source === target) return 1

  const sourceFamily = FAMILY[source]
  const targetFamily = FAMILY[target]
  if (sourceFamily === undefined || targetFamily === undefined) return null
  if (sourceFamily !== targetFamily) return null

  const sourcePer = PER_UNIT[source]
  const targetPer = PER_UNIT[target]
  if (sourcePer === undefined || targetPer === undefined) return null
  return sourcePer / targetPer
}

export interface DerivedCost {
  /** The cost in euros, or `null` when it cannot be worked out. */
  euros: number | null
  /** Why there is no figure — what the editor tells the user. */
  reason: 'ok' | 'no-price' | 'no-quantity' | 'incompatible-units'
}

/**
 * `quantity × price`, once both are expressed in the same unit.
 *
 * The price is quoted per `costUnit`; the row's quantity is in `unit`. Converting the quantity into
 * the priced unit is the only arithmetic here, and it refuses rather than guesses when the two are
 * different kinds of measure.
 */
export function derivedCost(
  row: { quantity: string; unit: string },
  item: { costPrice?: number | null; costUnit?: string | null; unit?: string | null } | null,
): DerivedCost {
  if (item === null || item.costPrice === null || item.costPrice === undefined) {
    return { euros: null, reason: 'no-price' }
  }

  const quantity = Number(row.quantity)
  if (!Number.isFinite(quantity) || quantity <= 0) return { euros: null, reason: 'no-quantity' }

  // A price with no unit of its own is quoted per the unit the item is counted in.
  const pricedPer = normalise(item.costUnit) === '' ? item.unit : item.costUnit
  const factor = conversionFactor(row.unit, pricedPer)
  if (factor === null) return { euros: null, reason: 'incompatible-units' }

  return { euros: quantity * factor * item.costPrice, reason: 'ok' }
}
